"use client";

import { useCallback, useRef, useState } from "react";
import {
  CheckCircle2,
  CircleDashed,
  Loader2,
  MinusCircle,
  Play,
  Square,
  XCircle,
} from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StreamingMarkdown } from "@/components/ui/streaming-markdown";
import {
  Task,
  TaskContent,
  TaskItem,
  TaskTrigger,
} from "@/components/ai-elements/task";
import {
  EMPTY_WORKFLOW_RUN,
  reduceWorkflowRun,
  type WorkflowRunState,
  type WorkflowRunStep,
} from "@/lib/ai/workflow-run-ui";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export type SiteWorkflow = {
  id: string;
  name: string;
  description: string;
  tags: string[];
  price: number;
  fields: {
    key: string;
    label: string;
    type: string;
    required: boolean;
    placeholder: string;
  }[];
  href: string;
};

const STEP_ICON = {
  running: Loader2,
  succeeded: CheckCircle2,
  failed: XCircle,
  skipped: MinusCircle,
} as const;

const STEP_TONE = {
  running: "text-foreground",
  succeeded: "text-muted-foreground",
  failed: "text-destructive",
  skipped: "text-muted-foreground/50",
} as const;

function StepRow({ step }: { step: WorkflowRunStep }) {
  const t = useTranslations("site.workflowRunner");
  const Icon = STEP_ICON[step.status];

  return (
    <TaskItem className={cn("space-y-1", STEP_TONE[step.status])}>
      <div className="flex items-center gap-2">
        <Icon
          className={cn("size-4 shrink-0", step.status === "running" && "animate-spin")}
        />
        <span className="font-medium">
          {step.title || t("node")}
          {step.iterLabel ? (
            <span className="ml-1 text-xs opacity-70">{step.iterLabel}</span>
          ) : null}
        </span>
        {step.durationMs > 0 ? (
          <span className="text-xs opacity-70">
            {(step.durationMs / 1000).toFixed(1)}s
          </span>
        ) : null}
        {step.costPoints > 0 ? (
          <span className="text-xs opacity-70">
            {t("stepPoints", { points: step.costPoints })}
          </span>
        ) : null}
      </div>

      {step.errorMessage ? (
        <p className="pl-6 text-xs text-destructive">{step.errorMessage}</p>
      ) : null}

      {/*
        节点自己吐出来的文字。只在它真的有内容时才显示——大多数节点（取数据、
        条件判断）是没有流式输出的，给它们留一块空白只会让步骤条看着松垮
      */}
      {step.text ? (
        <div className="pl-6">
          <StreamingMarkdown text={step.text} streaming={step.status === "running"} />
        </div>
      ) : null}
    </TaskItem>
  );
}

export function AiWorkflowRunner({
  workflow,
  loggedIn,
  loginHref,
}: {
  workflow: SiteWorkflow;
  loggedIn: boolean;
  loginHref: string;
}) {
  const t = useTranslations("site.workflowRunner");
  const [values, setValues] = useState<Record<string, string>>({});
  const [run, setRun] = useState<WorkflowRunState>(EMPTY_WORKFLOW_RUN);
  const abortRef = useRef<AbortController | null>(null);

  const runIdRef = useRef("");

  const running = run.status === "running";

  const stop = useCallback(() => {
    const runId = runIdRef.current;

    if (runId) {
      void apiFetch(
        `/api/member/ai/workflow/run/${encodeURIComponent(runId)}/cancel`,
        { method: "POST" },
      ).catch(() => {
        // 取消失败不影响下面断流：最坏是后台把这一条跑完
      });
    }

    abortRef.current?.abort();
    abortRef.current = null;
    setRun((current) =>
      reduceWorkflowRun(current, { type: "cancelled", message: t("cancelled") }),
    );
  }, [t]);

  async function start() {
    if (running) return;

    const controller = new AbortController();
    abortRef.current = controller;
    runIdRef.current = "";
    setRun({ ...EMPTY_WORKFLOW_RUN, status: "running" });

    try {
      const response = await fetch(
        `/api/member/ai/workflow/${encodeURIComponent(workflow.id)}/run`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ input: values }),
          signal: controller.signal,
        },
      );

      if (!response.body) {
        throw new Error(t("noResult"));
      }

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = "";

      for (;;) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += value;

        let split = buffer.indexOf("\n\n");

        while (split !== -1) {
          const frame = buffer.slice(0, split);
          buffer = buffer.slice(split + 2);
          split = buffer.indexOf("\n\n");

          const data = frame
            .split("\n")
            .find((line) => line.startsWith("data:"))
            ?.slice(5)
            .trim();

          if (!data) continue;

          try {
            const event = JSON.parse(data) as { type?: string };
            const type =
              event.type ??
              frame
                .split("\n")
                .find((line) => line.startsWith("event:"))
                ?.slice(6)
                .trim() ??
              "";

            setRun((current) => {
              const next = reduceWorkflowRun(current, { ...event, type });

              runIdRef.current = next.runId;

              return next;
            });
          } catch {
            // 半截帧：下一轮拼上再解析
          }
        }
      }
    } catch (error) {

      if (controller.signal.aborted) {
        return;
      }

      setRun((current) => ({
        ...current,
        status: "failed",
        errorMessage: error instanceof Error ? error.message : t("runFailed"),
      }));
    } finally {
      abortRef.current = null;
    }
  }

  if (!loggedIn) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {t("loginHint")}
        </p>
        <Button className="mt-4" render={<a href={loginHref} />}>
          {t("goLogin")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {workflow.fields.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("noInput")}</p>
        ) : null}

        {workflow.fields.map((field) => (
          <div key={field.key} className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor={`wf-${field.key}`}>
              {field.label}
              {field.required ? (
                <span className="ml-1 text-destructive">*</span>
              ) : null}
            </label>

            {/* 长文本给多行框：一句提示词塞进单行输入里没法看 */}
            {field.type === "json" || field.type === "array" ? (
              <Textarea
                id={`wf-${field.key}`}
                rows={4}
                placeholder={field.placeholder || (field.type === "json" ? "{}" : "[]")}
                value={values[field.key] ?? ""}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))
                }
              />
            ) : (
              <Input
                id={`wf-${field.key}`}
                type={field.type === "number" ? "number" : "text"}
                placeholder={field.placeholder}
                value={values[field.key] ?? ""}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))
                }
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {running ? (
          <Button variant="outline" onClick={stop}>
            <Square className="mr-2 size-4" />
            {t("stop")}
          </Button>
        ) : (
          <Button onClick={() => void start()}>
            <Play className="mr-2 size-4" />
            {t("run")}
          </Button>
        )}

        {workflow.price > 0 ? (
          <span className="text-xs text-muted-foreground">
            {t("price", { points: workflow.price })}
          </span>
        ) : null}
      </div>

      {/*
        步骤条与运行按钮之间留出一条分隔。原先只隔了一行间距，
        〔实测〕按钮与折叠标题贴得太近，想收起步骤时很容易点到「运行」上，
        而那时上一条还挂着，直接撞并发上限报错。
      */}
      {run.status !== "idle" ? (
        <Task className="rounded-lg border p-3" defaultOpen>
          <TaskTrigger
            title={
              run.status === "running"
                ? t("running", { count: run.steps.length })
                : run.status === "failed"
                  ? t("runFailed")
                  : run.costPoints > 0
                    ? t("doneWithCost", {
                        count: run.steps.length,
                        points: run.costPoints,
                      })
                    : t("done", { count: run.steps.length })
            }
          />
          <TaskContent>
            {run.steps.length === 0 ? (
              <TaskItem className="flex items-center gap-2">
                <CircleDashed className="size-4 animate-pulse" />
                {t("preparing")}
              </TaskItem>
            ) : null}

            {run.steps.map((step) => (
              <StepRow key={step.key} step={step} />
            ))}
          </TaskContent>
        </Task>
      ) : null}

      {run.errorMessage ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {run.errorMessage}
        </p>
      ) : null}

      {run.output ? (
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{t("result")}</p>
          <div className="rounded-lg border p-3">
            {Object.entries(run.output).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <p className="text-xs text-muted-foreground">{key}</p>
                <StreamingMarkdown
                  text={
                    typeof value === "string" ? value : JSON.stringify(value, null, 2)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
